import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Synthesis } from '../api/types';
import { SynthesisTable } from '../components/SynthesisTable';
import { CompiledSynthesisChart } from '../components/CompiledSynthesisChart';
import { SourceDiagramExportCard } from '../components/SourceDiagramExportCard';
import { CompiledExportToolbar } from '../components/CompiledExportToolbar';
import { DateRangeFilter } from '../components/DateRangeFilter';

interface Range {
  start: string;
  end: string;
}

/**
 * Reprend l'interface de visualisation d'un diagramme (bascule
 * Tableau/Diagramme, export PDF — voir DiagramPage) pour une synthèse
 * automatique. Le tableau reste PAR diagramme source (comme avant) ; le
 * diagramme graphique, lui, est un UNIQUE diagramme compilé fusionnant
 * toutes les tâches (voir CompiledSynthesisChart et ARCHITECTURE.md pour
 * le pourquoi — décision explicite de l'utilisateur, contrairement à la
 * version précédente qui affichait un GanttChart par diagramme). Structure
 * VOLONTAIREMENT identique à WorkloadPage (demande explicite
 * d'homogénéisation) : mêmes cartes par diagramme source
 * (`SourceDiagramExportCard`, visibles dans les deux vues), même export
 * du compilé (`CompiledExportToolbar`, visible seulement en vue Diagramme)
 * et même filtre de plage de dates (`DateRangeFilter`) — sans fenêtre par
 * défaut ici, contrairement à Charge de travail (voir FoldersService.getSynthesis).
 */
export function SynthesisPage() {
  const { t } = useTranslation();
  const { folderId, type } = useParams();
  const [data, setData] = useState<Synthesis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState<'table' | 'chart'>('table');
  const [appliedRange, setAppliedRange] = useState<Range | null>(null);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');

  const load = () => {
    if (!folderId || !type) return;
    setRefreshing(true);
    setError(null);
    const query = appliedRange ? `?start=${appliedRange.start}&end=${appliedRange.end}` : '';
    api
      .get<Synthesis>(`/folders/${folderId}/synthesis/${type}${query}`)
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setRefreshing(false));
  };

  useEffect(load, [folderId, type, appliedRange]);

  if (error) return <p className="error-text">{error}</p>;
  if (!data) return <p>{t('common.loading')}</p>;

  const rangeInvalid = !startInput || !endInput || startInput > endInput;
  const title = `${data.type} — ${t(`synthesis.labels.${data.type}`)} · ${data.folderName}`;

  return (
    <div>
      <Link to={`/folders/${folderId}`}>&larr; {t('diagram.backToFolder')}</Link>

      <div className="card diagram-toolbar" style={{ marginTop: 16, marginBottom: 16 }}>
        <strong className="diagram-name-display">{title}</strong>
        <button className="btn icon-btn" onClick={load} disabled={refreshing} title={t('synthesis.refresh') as string}>
          🔄
        </button>
      </div>

      <DateRangeFilter
        startInput={startInput}
        endInput={endInput}
        onStartInputChange={setStartInput}
        onEndInputChange={setEndInput}
        onApply={() => setAppliedRange({ start: startInput, end: endInput })}
        onReset={appliedRange ? () => setAppliedRange(null) : null}
        applyDisabled={rangeInvalid}
      />

      <div className="toolbar" style={{ marginBottom: 8 }}>
        <button className={`btn ${view === 'table' ? 'btn-primary' : ''}`} onClick={() => setView('table')}>
          {t('diagram.viewTable')}
        </button>
        <button className={`btn ${view === 'chart' ? 'btn-primary' : ''}`} onClick={() => setView('chart')}>
          {t('diagram.viewChart')}
        </button>
      </div>

      {view === 'chart' && (
        <div style={{ marginBottom: 24 }}>
          <CompiledExportToolbar groups={data.groups} title={title} />
          <CompiledSynthesisChart groups={data.groups} />
        </div>
      )}

      {data.groups.map((group) => (
        <div key={group.diagramId} style={{ marginBottom: 24 }}>
          <SourceDiagramExportCard diagramId={group.diagramId} diagramName={group.diagramName} assignee={group.assignee} />

          {view === 'table' &&
            (group.tasks.length === 0 ? (
              <p className="synthesis-empty">{t('synthesis.noTask')}</p>
            ) : (
              <SynthesisTable groups={[group]} hideGroupHeader />
            ))}
        </div>
      ))}
    </div>
  );
}
